import { inject, injectable, Store } from '@servicetitan/react-ioc';

import { makeObservable, observable, runInAction } from 'mobx';

import { UserApi, UserModel, Permissions, BuiltInUserRole } from './user.api';

export interface IUserStore extends Omit<UserModel, 'permissions'> {
    permissions: Set<Permissions>;
}

@injectable()
export class UserStore extends Store implements IUserStore {
    @observable userId?: number | undefined;
    @observable employeeId?: number | undefined;
    @observable userName?: string | undefined;
    @observable userRole?: BuiltInUserRole | undefined;
    @observable userCreatedOn?: Date | undefined;
    @observable name?: string | undefined;
    @observable avatar?: string | undefined;
    @observable phone?: string | undefined;
    @observable passwordExpired!: boolean;
    @observable passwordExpirationDueDays!: number;
    @observable isExternalUser!: boolean;
    @observable hasTitanAccess!: boolean;
    @observable isImpersonatedUser!: boolean;
    @observable isTemporaryPassword!: boolean;
    @observable isShowPasswordExperience!: boolean;
    @observable authenticationTicketId?: string | undefined;
    @observable isMultipleLoginsAllowed!: boolean;
    @observable permissions!: Set<Permissions>;

    constructor(@inject(UserApi) private readonly api: UserApi) {
        super();
        makeObservable(this);
    }

    async initialize() {
        const result = (await this.api.get()).data;

        runInAction(() => {
            this.userId = result.userId;
            this.employeeId = result.employeeId;
            this.userName = result.userName;
            this.userRole = result.userRole;
            this.userCreatedOn = result.userCreatedOn;
            this.name = result.name;
            this.avatar = result.avatar;
            this.phone = result.phone;
            this.passwordExpired = result.passwordExpired;
            this.passwordExpirationDueDays = result.passwordExpirationDueDays;
            this.isExternalUser = result.isExternalUser;
            this.hasTitanAccess = result.hasTitanAccess;
            this.isImpersonatedUser = result.isImpersonatedUser;
            this.isTemporaryPassword = result.isTemporaryPassword;
            this.isShowPasswordExperience = result.isShowPasswordExperience;
            this.authenticationTicketId = result.authenticationTicketId;
            this.isMultipleLoginsAllowed = result.isMultipleLoginsAllowed;
            this.permissions = new Set(result.permissions);
        });
    }

    hasPermission = (permission: Permissions) => {
        return this.userRole === BuiltInUserRole.Owner || this.permissions.has(permission);
    };
}
